<template>
  <div class="space-y-10">
    <Header v-if="isWeb" />

    <section class="panel animate-rise" data-testid="form-view">
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="game-field">
        <button
          v-for="option in gameOptions"
          :key="option.value"
          type="button"
          class="flex flex-col gap-1.5 rounded-2xl border p-4 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
          :class="gameMode === option.value
            ? 'bg-ink border-transparent text-white shadow-[0_4px_20px_-4px_rgba(5,46,22,0.3)]'
            : 'bg-white/80 border-ink/10 hover:border-ink/20 hover:bg-white'"
          :aria-pressed="gameMode === option.value"
          @click="gameMode = option.value"
          :data-testid="`game-option-${option.value}`"
        >
          <svg
            class="w-5 h-5 mb-0.5"
            :class="gameMode === option.value ? 'text-white/60' : 'text-slate-400'"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.75"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <template v-if="option.value === 'career'">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18M3 15h18M9 3v18" />
            </template>
            <template v-else-if="option.value === 'photo'">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </template>
            <template v-else>
              <line x1="18" y1="20" x2="18" y2="10" />
              <line x1="12" y1="20" x2="12" y2="4" />
              <line x1="6" y1="20" x2="6" y2="14" />
              <line x1="2" y1="20" x2="22" y2="20" />
            </template>
          </svg>
          <span
            class="text-xs font-bold uppercase tracking-widest"
            :class="gameMode === option.value ? 'text-white' : 'text-neutral-900'"
          >
            {{ option.title }}
          </span>
          <span
            class="text-sm leading-snug"
            :class="gameMode === option.value ? 'text-white/70' : 'text-slate-500'"
          >
            {{ option.label }}
          </span>
        </button>
      </div>

      <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div class="space-y-3">
          <p class="label">{{ t('form.league-label') }}</p>
          <select
            v-model="leagueSelection"
            class="input"
            data-testid="league-select"
          >
            <option value="">{{ t('form.random-option') }}</option>
            <option
              v-for="option in leagues"
              :key="option.label"
              :value="option.label"
            >
              {{ option.label }}
            </option>
          </select>
        </div>

        <div v-if="leagueSelection" class="space-y-3">
          <p class="label">{{ t('form.club-label') }}</p>
          <select v-model="teamId" class="input" data-testid="team-select">
            <option value="">{{ t('form.random-option') }}</option>
            <option
              v-for="team in clubOptions"
              :key="team.id"
              :value="String(team.id)"
            >
              {{ team.name }}
            </option>
          </select>
        </div>

        <div v-if="leagueSelection" class="space-y-3">
          <p class="label">{{ t('form.season-label') }}</p>
          <select
            v-model="seasonSelection"
            class="input"
            data-testid="season-select"
          >
            <option value="">{{ t('form.random-option') }}</option>
            <option v-for="label in seasonOptions" :key="label" :value="label">
              {{ label }}
            </option>
          </select>
        </div>
      </div>

      <div class="mt-6 space-y-3">
        <div class="flex items-center justify-between">
          <p class="label">{{ t('form.difficulty-label') }}</p>
        </div>
        <input
          v-model="difficultyIndex"
          type="range"
          min="0"
          :max="difficultyOptions.length - 1"
          step="1"
          class="w-full accent-ink"
          data-testid="difficulty-slider"
        />
        <div
          class="flex justify-between text-xs font-semibold uppercase tracking-[0.2em] text-slate-500"
        >
          <span :class="difficultyIndex === 0 ? 'text-neutral-900' : ''">{{ t('form.difficulty-easy') }}</span>
          <span :class="difficultyIndex === 1 ? 'text-neutral-900' : ''">{{ t('form.difficulty-medium') }}</span>
          <span :class="difficultyIndex === 2 ? 'text-neutral-900' : ''">{{ t('form.difficulty-hard') }}</span>
        </div>
      </div>

      <div
        class="mt-8 flex flex-col gap-4 items-center sm:flex-row sm:justify-center"
      >
        <button
          class="btn-primary w-full sm:w-auto"
          type="button"
          :disabled="store.loading"
          @click="handlePlay"
          data-testid="play-button"
        >
          <span v-if="store.loading">{{ t('common.loading') }}</span>
          <span v-else>{{ t('form.button-play') }}</span>
        </button>
        <p v-if="formError" class="text-sm font-semibold text-rose-600">
          {{ formError }}
        </p>
        <p v-else-if="store.error" class="text-sm font-semibold text-rose-600">
          {{ store.error }}
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { Capacitor } from "@capacitor/core";

const isWeb = Capacitor.getPlatform() === "web";
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { useGameStore } from "../stores/game";
import Header from "../components/Header.vue";
import { DIFFICULTY_LEVELS } from "../config/difficulty";
import { CLUBS } from "../data/data.clubs";
import { LEAGUES, leagueIdByLabel } from "../utils/leagues";
import { formatSeason, parseSeasonLabel, seasonLabels } from "../utils/season";
import { useText } from "../composables/useText.js";

const router = useRouter();
const store = useGameStore();
const { t } = useText();
const {
  leagueSelection,
  seasonSelection,
  difficultySelection,
  teamId,
  gameModeSelection,
} = storeToRefs(store);

const leagues = LEAGUES;
const seasons = seasonLabels();
const difficultyOptions = DIFFICULTY_LEVELS;
const gameOptions = [
  { value: "career", title: t('form.mode-career-short'), label: t('form.mode-career') },
  { value: "photo", title: t('form.mode-photo-short'), label: t('form.mode-photo') },
  { value: "stats", title: t('form.mode-stats-short'), label: t('form.mode-stats') },
];
const gameMode = computed({
  get: () => gameModeSelection.value || gameOptions[0].value,
  set: (value) => {
    gameModeSelection.value = value;
  },
});

const difficultyIndex = computed({
  get: () => {
    const index = difficultyOptions.findIndex(
      (option) => option.value === difficultySelection.value,
    );
    return index === -1 ? 0 : index;
  },
  set: (index) => {
    const safeIndex = Number.parseInt(index, 10);
    const option = difficultyOptions[safeIndex] ?? difficultyOptions[0];
    difficultySelection.value = option.value;
  },
});

const formError = ref("");
const selectedLeagueId = computed(() => {
  return leagueIdByLabel[leagueSelection.value] || null;
});
const leagueClubs = computed(() => {
  if (!selectedLeagueId.value) return [];
  const clubs = CLUBS[selectedLeagueId.value] || [];
  return clubs
    .map((club) => ({
      id: club.id,
      name: club.name,
      seasons: Array.isArray(club.seasons) ? club.seasons : []
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
});
const selectedClub = computed(() => {
  if (!teamId.value) return null;
  return (
    leagueClubs.value.find(
      (club) => String(club.id) === String(teamId.value)
    ) || null
  );
});
const clubOptions = computed(() => {
  if (!leagueSelection.value) return [];
  return leagueClubs.value;
});
const seasonOptions = computed(() => {
  if (!teamId.value) return seasons;

  const club = selectedClub.value;
  if (!club) return seasons;

  const mapped = club.seasons
    .map((year) => formatSeason(year))
    .filter(Boolean);
  const unique = new Set(mapped);
  if (seasonSelection.value && !unique.has(seasonSelection.value)) {
    unique.add(seasonSelection.value);
  }
  return Array.from(unique).sort(
    (a, b) => parseSeasonLabel(a) - parseSeasonLabel(b)
  );
});

watch(leagueSelection, (nextLeague, prevLeague) => {
  if (!nextLeague) {
    seasonSelection.value = "";
    teamId.value = "";
    return;
  }

  if (prevLeague && nextLeague !== prevLeague) {
    teamId.value = "";
  }
});

watch(teamId, (nextTeam, prevTeam) => {
  if (prevTeam !== undefined && nextTeam !== prevTeam) {
    seasonSelection.value = "";
  }
});

const handlePlay = async () => {
  formError.value = "";

  try {
    await store.startGame(
      leagueSelection.value,
      seasonSelection.value,
      teamId.value,
    );
    const playerId = store.player?.id;
    if (!playerId) {
      throw new Error(t('form.error-load-player'));
    }
    const resolvedSeasonLabel = store.season;
    const parsedSeasonYear = parseSeasonLabel(resolvedSeasonLabel);
    const seasonParam = Number.isNaN(parsedSeasonYear)
      ? String(resolvedSeasonLabel || "")
      : String(parsedSeasonYear);

    if (gameMode.value === "photo") {
      router.push({
        name: "photo",
        params: { playerId },
      });
      return;
    }

    if (gameMode.value === "stats") {
      router.push({
        name: "one-year",
        params: {
          playerId,
          season: seasonParam,
        },
      });
      return;
    }

    router.push({
      name: "data",
      params: { playerId },
    });
  } catch (error) {
    // error state handled in store
  }
};
</script>
